import * as Sentry from '@sentry/react';
import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { PermissionsType, SystemRolesType } from 'src/api';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import {
  handleErrorToast,
  handleSuccessToast,
  handleWarningToast,
} from 'src/components/molecules';
import {
  canToggleAdminPermission,
  canToggleSuperPermission,
  createAccounts,
  differenceInUserDetails,
  getEditablePermissions,
  initialValidation,
  isDifferenceInUserDetails,
  validateState,
} from 'src/components/organismsLegacy/EditUserModal/EditUserModal.helpers';
import {
  OrganisationRoleType,
  useCompleteAuditEntryMutation,
  useCreateAuditEntryMutation,
  usePutUserMutation,
} from 'src/graphql';
import { SYSTEM_PERMISSIONS } from 'src/permissionsTypes';
import { AuditActions, State, User, Validation } from 'src/types';
import { diff, TimeUnit } from 'src/utils/time';
import * as Integration from '../../data-integration/types';
import * as Types from '../../types';

interface UseEditUserModal {
  selectedUser: Partial<User> | null;
  show: boolean;
  handleClose: () => void;
  onSuccess?: () => void;
}

export const useEditUserModal = ({
  selectedUser,
  show,
  handleClose,
  onSuccess,
}: UseEditUserModal) => {
  const features = useSelector((state: State) => state.features);
  const isSlackEnabled = isRequiredFeatureEnabled(
    features,
    Types.FeatureFlag.SLACK_REPORTING,
  );
  const isAppEnabled = isRequiredFeatureEnabled(
    features,
    Types.FeatureFlag.APP_REPORTING,
  );

  const isEmployeeReportingDisabled = isRequiredFeatureEnabled(
    features,
    Types.FeatureFlag.DISABLE_EMPLOYEE_REPORTING,
  );

  const canGrantReportingAccess =
    (isAppEnabled || isSlackEnabled) && !isEmployeeReportingDisabled;

  const isSCIMOrg = useSelector(
    (state: Types.State) =>
      state.organisation.organisation?.activeScimProvider === true,
  );
  const initialUser: User = useMemo(
    () => ({
      name: '',
      email: '',
      eid: '',
      role: '',
      department: '',
      site: '',
      country: '',
      permissions:
        isSCIMOrg || !canGrantReportingAccess ? [] : [PermissionsType.APP],
      Accounts: [],
      SystemRoles: [],
      source: Types.UserSource.INTERNAL,
      translationEnabled: false,
      systemPermissions: [],
      ...selectedUser,
    }),
    [isSCIMOrg, canGrantReportingAccess, selectedUser],
  );
  const [user, setUser] = useState<User>(initialUser);
  const [validation, setValidation] = useState<Validation>(initialValidation);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [isRemovedDraftRelatedRole, setIsDraftRelatedRole] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const currentUser = useSelector(
    (state: State) => state.globalUsers.currentUser,
  );
  const organisationRoles = useSelector(
    (state: State) => state.user.organisationRoles,
  );

  const isInternalUser = selectedUser?.source !== 'GUEST_USER';
  const isNonEditableUser =
    isSCIMOrg && Boolean(selectedUser?.name) && isInternalUser;

  const disableUserDetails = useSelector(
    (state: State) =>
      state.integrationStatus.dataIntegration !== Integration.Types.NONE ||
      isNonEditableUser,
  );

  const { mutateAsync } = usePutUserMutation({
    onError: (error) => {
      if (error instanceof Error) {
        handleErrorToast({ toastMessage: error.message });
      }
    },
    onSuccess() {
      const isUserACaseManager = user?.SystemRoles?.some(
        (role) => role.publicSystemPermissions?.canBeAssignedAsCaseLead,
      );
      const isSelectedUserACaseManager =
        selectedUser?.systemPermissions?.includes(
          SYSTEM_PERMISSIONS.ASSIGNABLE_AS_CASE_LEAD,
        );

      if (!isUserACaseManager && isSelectedUserACaseManager) {
        handleWarningToast({
          toastMessage: 'Case Manager permission revoked',
          // eslint-disable-next-line max-len
          toastDescription: `Cases and Reports assigned to this user will be re-assigned to another Case Manager. Super Admins will receive a summary email of the re-assignments.`,
        });
      }
      onSuccess?.();
    },
  });
  const createAuditEntryMutation = useCreateAuditEntryMutation();
  const completeAuditEntry = useCompleteAuditEntryMutation();

  useEffect(() => {
    setUser(initialUser);
    setValidation({ ...initialValidation });
  }, [initialUser, selectedUser, selectedUser?.id, show]);

  useEffect(() => {
    if (!isEqual(user, selectedUser)) {
      setIsSaveDisabled(false);
    } else {
      setIsSaveDisabled(true);
    }
  }, [selectedUser, user]);

  const getCreateReportsRoleChange = (
    oldRoles: SystemRolesType[] | undefined,
    newRoles: SystemRolesType[],
    orgRoles: OrganisationRoleType[] | undefined,
  ) => {
    const newRolesContainCreateReportPermission = orgRoles?.find((orgRole) => {
      if (newRoles?.find((newRole) => newRole.name === orgRole.name)) {
        return Boolean(orgRole.publicSystemPermissions?.canCreateReport);
      }
    });

    const oldRolesContainCreateReportPermission = orgRoles?.find((orgRole) => {
      if (oldRoles?.find((oldRole) => oldRole.name === orgRole.name)) {
        return Boolean(orgRole.publicSystemPermissions?.canCreateReport);
      }
    });

    return Boolean(
      oldRolesContainCreateReportPermission &&
      !newRolesContainCreateReportPermission,
    );
  };

  useEffect(() => {
    const willLoseDraftReports = getCreateReportsRoleChange(
      selectedUser?.SystemRoles,
      user.SystemRoles,
      organisationRoles,
    );
    if (willLoseDraftReports) {
      setIsDraftRelatedRole(true);
    } else {
      setIsDraftRelatedRole(false);
    }
  }, [
    selectedUser,
    user.SystemRoles,
    user.systemPermissions,
    organisationRoles,
  ]);

  const queryClient = useQueryClient();
  const putUser = async () => {
    if (validateState(user, validation, setValidation)) {
      setIsLoading(true);
      const accounts = createAccounts(user);
      const updatedUserDetails = isDifferenceInUserDetails(user, selectedUser);
      const updatedUserPermissions = !isEqual(
        user.permissions,
        selectedUser?.permissions,
      );
      let updatedUserDetailsId: undefined | number;
      let updatedUserPermissionsId: undefined | number;

      if (updatedUserDetails) {
        const mutation = await createAuditEntryMutation.mutateAsync({
          auditAction: AuditActions.UPDATE_USER_DETAILS,
          auditData: {
            userId: user.id,
            changes: differenceInUserDetails(user, selectedUser),
          },
          completed: false,
        });
        updatedUserDetailsId = mutation.createAuditEntry?.id;
      }

      if (updatedUserPermissions) {
        const mutation = await createAuditEntryMutation.mutateAsync({
          auditAction: AuditActions.UPDATE_USER_PERMISSIONS,
          auditData: {
            userId: user.id,
            oldPermissions: selectedUser?.permissions,
            newPermissions: user.permissions,
          },
          completed: false,
        });
        updatedUserPermissionsId = mutation.createAuditEntry?.id;
      }

      try {
        await mutateAsync({
          putUserId: user.id,
          userData: {
            name: user.name,
            displayName: user.displayName,
            email: user.email,
            eid: user.eid,
            role: user.role,
            department: user.department,
            site: user.site,
            country: user.country,
            status: user.status,
            permissions: user.permissions,
            SystemRoles: user.SystemRoles.map(({ id, name, label }) => ({
              id,
              name,
              label,
            })),
            accounts: accounts.map((account) => ({
              ...account,
              Roles: undefined,
            })),
            source:
              isSCIMOrg && !selectedUser?.name ? 'GUEST_USER' : user.source,
          },
        });

        if (updatedUserDetailsId) {
          completeAuditEntry.mutate({ id: updatedUserDetailsId });
        }
        if (updatedUserPermissionsId) {
          completeAuditEntry.mutate({ id: updatedUserPermissionsId });
        }

        handleSuccessToast({
          toastMessage: selectedUser
            ? 'User edited successfully'
            : 'User created successfully',
        });
        queryClient.invalidateQueries('SearchUsersPaginated');
        queryClient.invalidateQueries(['GetUserRolesAuditEntries']);
        handleClose();
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const userStatus = selectedUser?.status;
  let remainingDays;
  if (userStatus === 'EXPIRING' && user.expiresAt) {
    remainingDays = Math.abs(
      diff(new Date(), Number(user.expiresAt), TimeUnit.days),
    );
  }

  const editablePermissions = Boolean(
    getEditablePermissions(currentUser, user),
  );
  const editableSuperPermissions =
    editablePermissions && Boolean(canToggleSuperPermission(currentUser, user));

  const editableAdminPermissions =
    editablePermissions && Boolean(canToggleAdminPermission(currentUser, user));

  const caseManagementText =
    selectedUser?.name &&
    user?.systemPermissions?.includes(
      SYSTEM_PERMISSIONS.ASSIGNABLE_AS_CASE_LEAD,
    )
      ? `Toggle off to revoke Case Manager access.
      Note that the cases and reports assigned to this user will be reassigned by the system.`
      : `Access and management capabilities to user's reports, only when assigned to the case manager.`;

  return {
    user,
    setUser,
    validation,
    setValidation,
    isSaveDisabled,
    disableUserDetails,
    putUser,
    userStatus,
    remainingDays,
    editablePermissions,
    editableSuperPermissions,
    editableAdminPermissions,
    currentUser,
    caseManagementText,
    isRemovedDraftRelatedRole,
    isLoading,
  };
};
